import {
  TrendingUp as ActivityIcon,
  Close as CloseIcon,
  FiberManualRecord as DotIcon,
  Person as PersonIcon,
  Schedule as TimeIcon,
} from "@mui/icons-material";
import {
  Avatar,
  Badge,
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Fade,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import React from "react";
import { Visit } from "../../store/types/api.types";

// Types
interface ActivitySummary {
  NewWorks: number;
  UpdatedWorks: number;
  NewWorkItems: number;
  UpdatedWorkItems: number;
}

interface CustomerInfo {
  Id: number | string;
  Name?: string;
  CustomerId?: string;
  OrgId?: string;
  Address?: string;
  CustomerType?: string;
  Archived?: boolean;
}

interface RecentProfilesModalProps {
  open: boolean;
  onClose: () => void;
  visits: Visit[];
  onSelect: (customerId: string | number) => void;
}

interface CustomerCardProps {
  visit: Visit;
  onSelect: (customerId: string | number) => void;
  index: number;
}

const getActivitySummary = (summary?: ActivitySummary): string => {
  if (!summary) return "Inga nya händelser";
  const items = [
    summary.NewWorks > 0 && `${summary.NewWorks} nya ärenden`,
    summary.UpdatedWorks > 0 && `${summary.UpdatedWorks} uppdaterade ärenden`,
    summary.NewWorkItems > 0 && `${summary.NewWorkItems} nya aktiviteter`,
    summary.UpdatedWorkItems > 0 &&
      `${summary.UpdatedWorkItems} uppdaterade aktiviteter`,
  ].filter(Boolean);
  return items.length ? items.join(" • ") : "Inga nya händelser";
};

const formatTimeAgo = (timeString: string): string => {
  const time = new Date(timeString);
  const now = new Date();
  const diffInHours = Math.floor(
    (now.getTime() - time.getTime()) / (1000 * 60 * 60)
  );

  if (diffInHours < 1) return "Mindre än 1 timme sedan";
  if (diffInHours < 24) return `${diffInHours} timmar sedan`;
  const diffInDays = Math.floor(diffInHours / 24);
  return `${diffInDays} dagar sedan`;
};

const getRandomColor = (name?: string | null): string => {
  const colors = [
    "#1976d2",
    "#388e3c",
    "#f57c00",
    "#7b1fa2",
    "#c2185b",
    "#00796b",
    "#455a64",
    "#e64a19",
  ];
  const index =
    name && name.length > 0 ? name.charCodeAt(0) % colors.length : 0;
  return colors[index];
};

const CustomerCard: React.FC<CustomerCardProps> = ({
  visit,
  onSelect,
  index,
}) => {
  const hasActivity = visit.HasNewActivity;
  const activityCount = visit.ActivitySummary
    ? (visit.ActivitySummary.NewWorks || 0) +
      (visit.ActivitySummary.UpdatedWorks || 0) +
      (visit.ActivitySummary.NewWorkItems || 0) +
      (visit.ActivitySummary.UpdatedWorkItems || 0)
    : 0;

  return (
    <Fade in timeout={300 + index * 100}>
      <Paper
        elevation={hasActivity ? 4 : 1}
        sx={{
          p: 2.5,
          cursor: "pointer",
          transition: "all 0.2s ease-in-out",
          background: hasActivity ? "#f8f9ff" : "#ffffff",
          border: hasActivity ? `2px solid #1976d2` : `1px solid #e0e0e0`,
          borderRadius: 2,
          position: "relative",
          overflow: "hidden",
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            borderColor: hasActivity ? "#1565c0" : "#1976d2",
          },
          "&::before": hasActivity
            ? {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: 4,
                background: "#1976d2",
              }
            : {},
        }}
        onClick={() => onSelect(visit.Customer.Id)}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Badge
            badgeContent={hasActivity ? activityCount : 0}
            color="primary"
            overlap="circular"
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
            sx={{
              "& .MuiBadge-badge": {
                fontSize: "0.7rem",
                height: 18,
                minWidth: 18,
                fontWeight: 600,
              },
            }}
          >
            <Avatar
              sx={{
                width: 48,
                height: 48,
                bgcolor: getRandomColor(visit.Customer.Name),
                fontSize: "1.2rem",
                fontWeight: 600,
                color: "white",
              }}
            >
              {visit.Customer.Name?.charAt(0) || <PersonIcon />}
            </Avatar>
          </Badge>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: hasActivity ? 600 : 500,
                  color: hasActivity ? "#1976d2" : "#000000",
                  fontSize: "1rem",
                }}
                noWrap
              >
                {visit.Customer.Name ||
                  visit.Customer.CustomerId ||
                  "Okänd kund"}
              </Typography>
              {hasActivity && (
                <DotIcon
                  sx={{
                    color: "#1976d2",
                    fontSize: 10,
                  }}
                />
              )}
            </Stack>

            <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
              <TimeIcon sx={{ fontSize: 14, color: "#666666" }} />
              <Typography
                variant="caption"
                sx={{ color: "#666666", fontSize: "0.75rem" }}
              >
                {formatTimeAgo(visit.Time)}
              </Typography>
            </Stack>

            <Stack direction="row" alignItems="center" spacing={1}>
              <ActivityIcon sx={{ fontSize: 14, color: "#666666" }} />
              <Typography
                variant="body2"
                sx={{
                  color: hasActivity ? "#1976d2" : "#666666",
                  fontWeight: hasActivity ? 500 : 400,
                  fontSize: "0.85rem",
                }}
              >
                {getActivitySummary(visit.ActivitySummary)}
              </Typography>
            </Stack>
          </Box>
        </Stack>

        {hasActivity && (
          <Chip
            label="Nya händelser"
            size="small"
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              fontSize: "0.7rem",
              height: 22,
              fontWeight: 500,
              bgcolor: "#1976d2",
              color: "white",
              "& .MuiChip-label": {
                px: 1,
              },
            }}
          />
        )}
      </Paper>
    </Fade>
  );
};

const RecentProfilesModal: React.FC<RecentProfilesModalProps> = ({
  open,
  onClose,
  visits,
  onSelect,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 16px 48px rgba(0,0,0,0.2)",
          background: "#ffffff",
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 2,
          background: "#ffffff",
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="h5" sx={{ fontWeight: 600, color: "#1976d2" }}>
            Senaste kunder
          </Typography>
          <IconButton
            onClick={onClose}
            sx={{
              bgcolor: "#f5f5f5",
              "&:hover": { bgcolor: "#e0e0e0" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
        <Typography variant="body2" sx={{ mt: 1, color: "#666666" }}>
          Klicka på en kund för att öppna deras profil
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {visits.length === 0 ? (
          <Paper
            sx={{
              p: 4,
              textAlign: "center",
              bgcolor: "#f8f9fa",
              border: "2px dashed #dee2e6",
              borderRadius: 2,
            }}
          >
            <PersonIcon sx={{ fontSize: 48, color: "#6c757d", mb: 2 }} />
            <Typography variant="h6" sx={{ color: "#6c757d", mb: 1 }}>
              Inga besökta kunder ännu
            </Typography>
            <Typography variant="body2" sx={{ color: "#6c757d" }}>
              Besöka en kund först för att se dem här
            </Typography>
          </Paper>
        ) : (
          <Stack spacing={2}>
            {visits.map((visit, index) => (
              <CustomerCard
                key={visit.Customer.Id}
                visit={visit}
                onSelect={onSelect}
                index={index}
              />
            ))}
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RecentProfilesModal;

// Export types for use in other files
export type { ActivitySummary, CustomerInfo, RecentProfilesModalProps, Visit };
